import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, X, Loader2, Car, Volume2, Ear, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useVoicePipeline } from "../../hooks/useVoicePipeline";
import { parseCommand, isAffirmative, isNegative, unlockAudio } from "../../lib/voice";
import { ondiApi } from "../../lib/ondi";
import { chatApi } from "../../lib/realtime";
import { api } from "../../lib/api";

/**
 * Voice Overlay for Modo Conducción (hands-free) — Fase 4b.
 *
 * VISUAL:
 *   - Fullscreen (mobile) / anchored drawer (desktop) with big-contrast type,
 *     centered ONDI avatar, live waveform canvas driven by AnalyserNode.
 *
 * STATES: idle / listening / thinking / speaking (drives visuals).
 *
 * PRIVACY:
 *   The mic is used ONLY for local voice-command recognition. No audio is
 *   uploaded or transmitted to other users. No voice calls, no video, no GPS.
 */

const STATE_LABELS = {
  idle: "Toca el micrófono",
  listening: "Escuchando…",
  thinking: "Procesando…",
  speaking: "ONDI hablando…",
};

/* -------------------- Waveform Canvas -------------------- */
function WaveformCanvas({ analyserRef, active }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const accent = () => {
      try {
        return (
          getComputedStyle(document.documentElement).getPropertyValue("--accent").trim() ||
          "#06B6D4"
        );
      } catch (_e) {
        return "#06B6D4";
      }
    };

    const draw = () => {
      const analyser = analyserRef?.current;
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      const color = accent();
      if (!active || !analyser) {
        ctx.strokeStyle = "rgba(255,255,255,0.06)";
        ctx.lineWidth = 2 * dpr;
        ctx.beginPath();
        ctx.moveTo(0, h / 2);
        ctx.lineTo(w, h / 2);
        ctx.stroke();
      } else {
        const data = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(data);
        ctx.strokeStyle = color;
        ctx.lineWidth = 2.2 * dpr;
        ctx.shadowColor = color;
        ctx.shadowBlur = 10 * dpr;
        ctx.beginPath();
        const step = w / data.length;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          const y = h / 2 + v * (h / 2 - 4 * dpr);
          if (i === 0) ctx.moveTo(0, y);
          else ctx.lineTo(i * step, y);
        }
        ctx.stroke();
        ctx.shadowBlur = 0;
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [analyserRef, active]);

  return (
    <canvas
      ref={canvasRef}
      className="w-full h-24"
      data-testid="voice-waveform"
      aria-hidden="true"
    />
  );
}

/* -------------------- Avatar (state-reactive) -------------------- */
function OndiVoiceAvatar({ state, audioLevel }) {
  const pulse = state === "listening" || state === "dictating" || state === "confirming";
  const dynScale = pulse ? 1 + Math.min(0.35, audioLevel * 2.5) : 1;
  const speakingWobble = state === "speaking" ? 1 + Math.sin(Date.now() / 180) * 0.04 : 1;
  const scale = state === "speaking" ? speakingWobble : dynScale;

  return (
    <div
      className="relative flex items-center justify-center h-44 w-44"
      data-testid="voice-avatar"
      data-state={state}
    >
      <div
        className="absolute inset-0 rounded-full transition-transform duration-100"
        style={{
          background:
            "radial-gradient(circle, var(--accent-soft) 0%, transparent 65%)",
          transform: `scale(${scale})`,
          opacity: state === "idle" ? 0.35 : 0.9,
        }}
      />
      {state === "thinking" && (
        <div
          className="absolute inset-3 rounded-full border-2 border-dashed animate-spin"
          style={{ borderColor: "var(--accent)", animationDuration: "3s" }}
        />
      )}
      {state === "listening" && (
        <div
          className="absolute inset-6 rounded-full animate-ping"
          style={{ background: "var(--accent-soft)", opacity: 0.5 }}
        />
      )}
      <div
        className="relative h-28 w-28 rounded-full flex items-center justify-center q-glass border border-white/20"
        style={{ boxShadow: "0 0 48px var(--accent-soft)" }}
      >
        {state === "speaking" ? (
          <Volume2 size={48} className="q-accent-text" />
        ) : state === "thinking" ? (
          <Loader2 size={48} className="q-accent-text animate-spin" />
        ) : (
          <Ear size={48} className="q-accent-text" />
        )}
      </div>
    </div>
  );
}

/* -------------------- Main component -------------------- */
export function OndiVoiceMode({ open, onClose }) {
  const [uxState, setUxState] = useState("idle"); // uxState: idle | dictating | confirming — mapped to pipeline state
  const [pendingReply, setPendingReply] = useState(null);
  const [suggestions, setSuggestions] = useState([]); // {text, convId, target}
  const [lastSpoken, setLastSpoken] = useState("");
  const [runningIntent, setRunningIntent] = useState(null); // 'command' | 'dictate' | 'confirm' | 'pick_option'
  const openRef = useRef(open);
  const runningIntentRef = useRef(runningIntent);
  const pendingReplyRef = useRef(pendingReply);
  const suggestionsRef = useRef(suggestions);

  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    runningIntentRef.current = runningIntent;
  }, [runningIntent]);
  useEffect(() => {
    pendingReplyRef.current = pendingReply;
  }, [pendingReply]);
  useEffect(() => {
    suggestionsRef.current = suggestions;
  }, [suggestions]);

  const onFinalTranscript = useCallback(async (text) => {
    const mode = runningIntentRef.current;
    if (!openRef.current) return;
    if (mode === "dictate") {
      const target = pendingReplyRef.current;
      if (!target) {
        setRunningIntent(null);
        return;
      }
      startConfirmSend({ ...target, text });
      return;
    }
    if (mode === "confirm") {
      handleConfirmation(text);
      return;
    }
    if (mode === "pick_option") {
      handleOptionPick(text);
      return;
    }
    // Default: interpret as a command
    handleCommand(text);
  }, []);

  const onPipelineError = useCallback((code) => {
    if (code === "not-allowed") {
      toast.error("Necesito permiso del micrófono para escucharte.");
    } else if (code === "stt-unsupported") {
      toast.error("Tu navegador no soporta reconocimiento de voz.");
    } else if (code === "no-speech") {
      // silent — user just didn't talk
    }
  }, []);

  const pipeline = useVoicePipeline({
    lang: "es-MX",
    onFinalTranscript,
    onError: onPipelineError,
    bargeInEnabled: true,
    active: open,
  });

  const { state, transcript, audioLevel, analyserRef, supported, micError, startListening, stopListening, speak, cancelSpeaking } = pipeline;

  /* ---------------- Intents ---------------- */

  const say = useCallback(
    async (text, { autoListenAfter = true } = {}) => {
      setLastSpoken(text);
      await speak(text, { autoListenAfter: autoListenAfter && openRef.current });
    },
    [speak]
  );

  const askForCommand = useCallback(() => {
    setRunningIntent("command");
    startListening();
  }, [startListening]);

  const askForDictation = useCallback(() => {
    setRunningIntent("dictate");
    startListening();
  }, [startListening]);

  const askForConfirmation = useCallback(() => {
    setRunningIntent("confirm");
    startListening();
  }, [startListening]);

  const askForOptionPick = useCallback(() => {
    setRunningIntent("pick_option");
    startListening();
  }, [startListening]);

  const exitDriving = useCallback(async () => {
    cancelSpeaking();
    stopListening();
    setRunningIntent(null);
    setPendingReply(null);
    setSuggestions([]);
    onClose?.();
  }, [cancelSpeaking, stopListening, onClose]);

  /* ---------------- Read Inbox ---------------- */
  const handleReadInbox = useCallback(async () => {
    try {
      const data = await ondiApi.inbox();
      const items = data.items || [];
      const total = data.total_unread || 0;
      if (items.length === 0) {
        await say("No tienes mensajes sin leer. Bandeja limpia. ¿Qué más te ayudo?");
        setRunningIntent("command");
        return;
      }
      const header = total === 1 ? "Tienes 1 mensaje sin leer." : `Tienes ${total} mensajes sin leer.`;
      const lines = [header];
      for (const it of items.slice(0, 5)) {
        const from = it.conversation_type === "group"
          ? `${it.sender_display_name} en ${it.conversation_title || "un grupo"}`
          : it.sender_display_name;
        lines.push(`De ${from}: ${truncate(it.text, 120)}.`);
      }
      const first = items[0];
      lines.push(`¿Quieres responder a ${first.sender_display_name}?`);
      await speak(lines.join(" "), { autoListenAfter: false });
      if (!openRef.current) return;
      setPendingReply({
        convId: first.conversation_id,
        target: first.sender_display_name,
      });
      askForConfirmation();
    } catch (_err) {
      await say("No pude leer tu bandeja. Intenta de nuevo.");
      setRunningIntent("command");
    }
  }, [say, speak, askForConfirmation]);

  /* ---------------- Reply (target + dictation) ---------------- */
  const findConversationByTarget = useCallback(async (target) => {
    const normalized = target.replace(/^@/, "").trim().toLowerCase();
    try {
      const res = await api.get("/users/search", { params: { q: normalized } });
      const arr = res.data || [];
      if (arr.length > 0) {
        const other = arr[0];
        const convRes = await api.post("/conversations", { to_user_id: other.id });
        return { convId: convRes.data.id, targetName: other.display_name };
      }
    } catch (_e) {
      /* ignore */
    }
    try {
      const convs = await chatApi.listConversations();
      const first = normalized.split(" ")[0];
      const hit = convs.find((c) => {
        if (c.type === "group") return (c.title || "").toLowerCase().includes(first);
        const dn = (c.other?.display_name || "").toLowerCase();
        return dn.startsWith(first) || dn.includes(normalized);
      });
      if (hit) {
        const nm = hit.type === "group" ? hit.title : hit.other?.display_name;
        return { convId: hit.id, targetName: nm };
      }
    } catch (_e) {
      /* ignore */
    }
    return null;
  }, []);

  const handleReply = useCallback(
    async (target, dictated = null) => {
      const found = await findConversationByTarget(target);
      if (!found) {
        await say(`No encontré a ${target} en tus contactos. ¿Alguien más?`);
        setRunningIntent("command");
        return;
      }
      if (dictated) {
        startConfirmSend({ convId: found.convId, target: found.targetName, text: dictated });
        return;
      }
      setPendingReply({ convId: found.convId, target: found.targetName });
      await speak(
        `Va. ¿Qué le digo a ${found.targetName}? Puedes dictarlo, o decir "opciones" para sugerencias.`,
        { autoListenAfter: false }
      );
      if (!openRef.current) return;
      askForDictation();
    },
    [findConversationByTarget, say, speak, askForDictation]
  );

  /* ---------------- Suggested options (LLM smart replies) ---------------- */
  const handleAskSuggestions = useCallback(async () => {
    const target = pendingReplyRef.current;
    if (!target) {
      await say("Primero dime a quién quieres responder.");
      setRunningIntent("command");
      return;
    }
    try {
      const data = await ondiApi.suggest(target.convId);
      const arr = (data.suggestions || []).slice(0, 3);
      if (arr.length === 0) {
        await say("No tengo sugerencias en este momento. Dicta tu mensaje libremente.");
        askForDictation();
        return;
      }
      const packed = arr.map((s, i) => ({ text: s, convId: target.convId, target: target.target, index: i + 1 }));
      setSuggestions(packed);
      const lines = ["Aquí van tres opciones."];
      packed.forEach((p) => lines.push(`Opción ${p.index}: ${p.text}.`));
      lines.push("Di opción uno, dos, tres, o dicta la tuya.");
      await speak(lines.join(" "), { autoListenAfter: false });
      if (!openRef.current) return;
      askForOptionPick();
    } catch (_e) {
      await say("No pude cargar sugerencias. Dicta tu mensaje.");
      askForDictation();
    }
  }, [say, speak, askForDictation, askForOptionPick]);

  const handleOptionPick = useCallback(
    async (raw) => {
      const cmd = parseCommand(raw);
      if (cmd.intent === "pick_option") {
        const pick = suggestionsRef.current.find((s) => s.index === cmd.option);
        if (pick) {
          startConfirmSend({ convId: pick.convId, target: pick.target, text: pick.text });
          return;
        }
      }
      if (cmd.intent === "exit") {
        exitDriving();
        return;
      }
      // Treat as free dictation instead
      const target = pendingReplyRef.current;
      if (target) {
        startConfirmSend({ ...target, text: raw });
        return;
      }
      await say('Di "opción uno", "opción dos" o "opción tres".');
      askForOptionPick();
    },
    [say, exitDriving, askForOptionPick]
  );

  /* ---------------- Confirm & send ---------------- */
  const startConfirmSend = useCallback(
    async ({ convId, target, text }) => {
      const clean = (text || "").trim();
      if (!clean) {
        await say("No entendí nada. Repite tu mensaje, por favor.");
        askForDictation();
        return;
      }
      setPendingReply({ convId, target, text: clean });
      await speak(`Le voy a decir a ${target}: ${clean}. ¿Lo envío?`, { autoListenAfter: false });
      if (!openRef.current) return;
      askForConfirmation();
    },
    [say, speak, askForConfirmation, askForDictation]
  );

  const handleConfirmation = useCallback(
    async (raw) => {
      if (isAffirmative(raw)) {
        const p = pendingReplyRef.current;
        if (!p || !p.text) {
          setRunningIntent("command");
          return;
        }
        try {
          await chatApi.sendMessage(p.convId, p.text);
          setPendingReply(null);
          setSuggestions([]);
          await say(`Enviado a ${p.target}. ¿Algo más?`);
          setRunningIntent("command");
        } catch (_e) {
          await say("No pude enviarlo. ¿Lo intento de nuevo?");
          askForConfirmation();
        }
        return;
      }
      if (isNegative(raw)) {
        setPendingReply(null);
        setSuggestions([]);
        await say("Va, cancelado. ¿Qué más te ayudo?");
        setRunningIntent("command");
        return;
      }
      await say('No te entendí. Di sí o no.');
      askForConfirmation();
    },
    [say, askForConfirmation]
  );

  /* ---------------- Command dispatcher ---------------- */
  const handleCommand = useCallback(
    async (raw) => {
      const cmd = parseCommand(raw);
      if (cmd.intent === "exit") {
        await say("Modo conducción desactivado.", { autoListenAfter: false });
        exitDriving();
        return;
      }
      if (cmd.intent === "read_inbox") {
        handleReadInbox();
        return;
      }
      if (cmd.intent === "reply") {
        handleReply(cmd.target, cmd.text || null);
        return;
      }
      if (cmd.intent === "suggest_options") {
        handleAskSuggestions();
        return;
      }
      await say(
        'No te entendí. Puedes decir "lee mis mensajes", "responde a" y un nombre, o "salir".'
      );
      setRunningIntent("command");
    },
    [say, exitDriving, handleReadInbox, handleReply, handleAskSuggestions]
  );

  /* ---------------- Kickoff on open ---------------- */
  useEffect(() => {
    if (!open) return undefined;
    if (!supported.stt) return undefined;
    if (micError) return undefined;
    let cancelled = false;
    (async () => {
      await say(
        'Modo Conducción activo. Di "lee mis mensajes", "responde a" y un nombre, o "salir".',
        { autoListenAfter: false }
      );
      if (cancelled || !openRef.current) return;
      askForCommand();
    })();
    return () => {
      cancelled = true;
    };
  }, [open, supported.stt, micError]);

  /* ---------------- Manual mic button ---------------- */
  const onMicPress = useCallback(() => {
    // FIRST user gesture inside the voice overlay — unlock iOS audio here.
    try {
      unlockAudio();
    } catch (_e) {
      /* ignore */
    }
    if (state === "speaking") {
      cancelSpeaking();
      return;
    }
    if (state === "listening") {
      stopListening();
      return;
    }
    // Restart current intent (default to command listening)
    setRunningIntent(runningIntent || "command");
    startListening();
  }, [state, cancelSpeaking, stopListening, startListening, runningIntent]);

  if (!open) return null;

  const disabled = !supported.stt || !!micError;
  const unsupportedMessage = !supported.stt
    ? "Tu navegador no soporta reconocimiento de voz. Usa Chrome o Edge en escritorio o Android para el Modo Conducción."
    : micError === "not-allowed"
    ? "Necesito permiso del micrófono. Actívalo en el candado de la barra de direcciones y vuelve a abrir el Modo Conducción."
    : micError === "mediadevices-unsupported"
    ? "Tu navegador no expone el micrófono en este contexto (¿estás en HTTP?). Usa la versión segura HTTPS."
    : null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-950 flex flex-col"
      data-testid="voice-mode"
      role="dialog"
      aria-label="Modo Conducción"
    >
      {/* Header */}
      <header className="q-glass border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <div
          className="h-11 w-11 rounded-2xl flex items-center justify-center border border-white/10"
          style={{ background: "var(--accent-soft)", boxShadow: "0 0 24px var(--accent-soft)" }}
        >
          <Car size={22} className="q-accent-text" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-lg font-bold tracking-tight truncate">Modo Conducción</div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400">
            manos libres · voz local
          </div>
        </div>
        <button
          type="button"
          onClick={exitDriving}
          className="text-slate-200 hover:text-white p-2 rounded-full border border-white/10"
          aria-label="Salir del Modo Conducción"
          data-testid="voice-exit"
        >
          <X size={22} />
        </button>
      </header>

      {/* Body */}
      <div className="flex-1 flex flex-col items-center justify-start px-6 py-4 gap-5 text-center overflow-y-auto">
        {unsupportedMessage ? (
          <div className="max-w-md mt-8" data-testid="voice-support-error">
            <div className="text-2xl font-bold q-accent-text mb-2">Voz no disponible</div>
            <p className="text-base text-slate-200 leading-relaxed">{unsupportedMessage}</p>
            <p className="mt-3 text-sm text-slate-400">
              Puedes seguir usando ONDI en modo texto normal.
            </p>
            <button
              type="button"
              onClick={exitDriving}
              className="q-btn-primary mt-6 !py-2 !px-5 inline-flex items-center gap-2"
              data-testid="voice-back-to-text"
            >
              <MessageSquare size={16} /> Volver al chat de texto
            </button>
          </div>
        ) : (
          <>
            <OndiVoiceAvatar state={state} audioLevel={audioLevel} />

            <div
              className="text-3xl sm:text-4xl font-bold tracking-tight q-accent-text"
              data-testid="voice-state-label"
            >
              {STATE_LABELS[state] || STATE_LABELS.idle}
            </div>

            {/* Live waveform */}
            <div className="w-full max-w-md rounded-2xl q-glass border border-white/10 p-2">
              <WaveformCanvas
                analyserRef={analyserRef}
                active={state === "listening" || state === "speaking"}
              />
            </div>

            {/* Live transcript */}
            <div
              className="min-h-[3rem] text-lg text-slate-100 max-w-md break-words px-2"
              data-testid="voice-transcript"
            >
              {transcript || (
                <span className="text-slate-500 italic">
                  {state === "idle"
                    ? "Toca el micrófono cuando quieras hablar."
                    : state === "listening"
                    ? "…"
                    : ""}
                </span>
              )}
            </div>

            {/* Last spoken (what ONDI just said) */}
            {lastSpoken && (
              <div
                className="max-w-md text-sm text-slate-400 flex items-start gap-2 text-left"
                data-testid="voice-last-spoken"
              >
                <Volume2 size={16} className="mt-0.5 shrink-0 q-accent-text" />
                <span>{lastSpoken}</span>
              </div>
            )}

            {/* Pending outbound preview */}
            {pendingReply?.text && (
              <div
                className="max-w-md q-glass border border-white/10 rounded-2xl p-3 text-sm text-slate-100 w-full text-left"
                data-testid="voice-pending-preview"
              >
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1">
                  A {pendingReply.target}
                </div>
                <div>&ldquo;{pendingReply.text}&rdquo;</div>
              </div>
            )}

            {/* Suggested options list (for "opción uno/dos/tres") */}
            {suggestions.length > 0 && (
              <div
                className="max-w-md w-full text-left space-y-1"
                data-testid="voice-suggestions"
              >
                {suggestions.map((s) => (
                  <div
                    key={s.index}
                    className="q-glass border border-white/10 rounded-2xl p-2 text-sm flex items-center gap-3"
                    data-testid={`voice-option-${s.index}`}
                  >
                    <span
                      className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: "var(--accent)", color: "var(--accent-contrast)" }}
                    >
                      {s.index}
                    </span>
                    <span className="flex-1 text-slate-100">{s.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Big mic button */}
            <button
              type="button"
              onClick={onMicPress}
              disabled={disabled}
              className={
                "mt-2 h-32 w-32 sm:h-36 sm:w-36 rounded-full flex items-center justify-center " +
                "border-4 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              }
              style={{
                borderColor: "var(--accent)",
                background:
                  state === "listening" ? "var(--accent)" : "transparent",
                boxShadow:
                  state === "listening"
                    ? "0 0 80px var(--accent-soft), 0 0 20px var(--accent)"
                    : "0 0 30px var(--accent-soft)",
              }}
              aria-label={state === "listening" ? "Detener escucha" : "Empezar a escuchar"}
              data-testid="voice-mic-btn"
            >
              {state === "thinking" ? (
                <Loader2 size={64} className="animate-spin text-white" />
              ) : state === "listening" ? (
                <Mic size={64} style={{ color: "var(--accent-contrast)" }} />
              ) : (
                <MicOff size={64} className="q-accent-text" />
              )}
            </button>
          </>
        )}
      </div>

      {/* Command hints */}
      {!unsupportedMessage && (
        <div className="border-t border-white/10 px-4 py-3 text-center">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">
            Comandos
          </div>
          <div className="flex flex-wrap justify-center gap-2 text-xs">
            <span className="q-chip">&ldquo;Lee mis mensajes&rdquo;</span>
            <span className="q-chip">&ldquo;Responde a Luna&rdquo;</span>
            <span className="q-chip">&ldquo;Opciones&rdquo;</span>
            <span className="q-chip">&ldquo;Salir&rdquo;</span>
          </div>
        </div>
      )}
    </div>
  );
}

function truncate(s, n) {
  if (!s) return "";
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}
